// src/pages/admin/AdminCostDashboard.jsx
// Route: /admin/costs (mounted in App.jsx, wrapped in <AdminRoute />).
//
// Founder-only dashboard for tracking the two largest variable costs:
//   • SMS sends (Twilio + 10DLC fees)
//   • AI message generation (Anthropic API)
//
// Why this exists:
//   Phase 1 posture intentionally has NO plan enforcement (per pricing-
//   strategy.md). All quota gates are off, every user gets the top-tier
//   experience. That means the only protection against runaway costs
//   right now is "the founder eyeballs it." This page is those eyeballs.
//
//   Also feeds the Phase 3 debate about what the actual tier limits
//   should be — without real usage data, the TIER_LIMITS table in
//   tierLimits.js is just guesses. Once we have a few months of this
//   dashboard's data, the limits can be set with evidence.
//
// What it shows:
//   • Headline: this-month total SMS, AI messages, estimated cost
//   • Top-N teams by SMS volume (with plan + estimated cost)
//   • Top-N teams by AI generation (with plan + estimated cost)
//   • Month picker to backtrack
//
// Cost estimates (in code constants below — update if Twilio/Anthropic
// pricing changes):
//   • SMS: $0.0079 base + $0.005 10DLC = ~$0.0129/segment, rounded $0.013
//     Most messages are 1-2 segments, average ~1.5 → $0.02/message
//   • AI: claude-sonnet-5 pricing (~$3 input / $15 output per 1M tokens;
//     intro $2/$10 through 2026-08-31, so this slightly overestimates
//     until then). Each generateMessage uses ~500 input + ~300 output
//     tokens → ~$0.0015 input + $0.0045 output = ~$0.006/message
//
// Data source:
//   • collectionGroup('smsUsage') — every team's per-month SMS counter
//   • collectionGroup('aiUsage')  — every team's per-month AI counter
//   Both writes are server-only, so the numbers are tamper-proof.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collectionGroup, query, where, orderBy, limit, getDocs, doc, getDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import {
  ChartBarIcon,
  ChevronLeftIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// ── Cost estimates (per unit, in USD) ────────────────────────────────
// Keep these in sync with actual provider pricing. Twilio + 10DLC fees
// vary by carrier — the 0.013 figure is a blended average for US A2P
// traffic as of early 2026. Anthropic pricing is per-1M-tokens with our
// current model + average prompt/response shape factored in.
const COST_PER_SMS_USD = 0.013;
const COST_PER_AI_MSG_USD = 0.006;

const TOP_N = 20;

function getCurrentMonthKey() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Generate the last 12 months as picker options. Always includes the
// current month at the top so the default view is "this month."
function lastTwelveMonthKeys() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function formatMonthLabel(monthKey) {
  if (!monthKey || monthKey.length !== 7) return monthKey;
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function formatUsd(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

// Plan badge classes — match the lime/pitch palette so the dashboard
// reads consistently with the rest of the app.
const PLAN_BADGE = {
  free:       'bg-zinc-100 text-zinc-600 border-zinc-200',
  team:       'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40',
  coach:      'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40',
  pro:        'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40',
  club:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  growth:     'bg-blue-50 text-blue-700 border-blue-200',
  elite:      'bg-purple-50 text-purple-700 border-purple-200',
  enterprise: 'bg-amber-50 text-amber-800 border-amber-200',
};

function PlanBadge({ plan }) {
  const key = String(plan || 'free').toLowerCase();
  const cls = PLAN_BADGE[key] || PLAN_BADGE.free;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}
      style={{ fontFamily: HEAD_FONT }}
    >
      {key}
    </span>
  );
}

// Hydrates team names by reading the team docs after the usage docs come
// back. Cached per page-load so re-rendering on month change doesn't
// re-fetch what's already known. Returns a `Map<teamId, name>`.
async function hydrateTeamNames(teamIds, cache) {
  const missing = teamIds.filter((id) => !cache.has(id));
  if (missing.length === 0) return cache;
  // Firestore has a 30-doc limit on `in` queries — but here we're doing
  // individual gets (no `in`), so any number is fine. We do them in
  // parallel for speed.
  const snaps = await Promise.all(
    missing.map((id) => getDoc(doc(db, 'teams', id)).catch(() => null))
  );
  snaps.forEach((s, i) => {
    cache.set(missing[i], s?.exists() ? (s.data()?.name || missing[i]) : missing[i]);
  });
  return cache;
}

export default function AdminCostDashboard() {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey());
  const [smsRows, setSmsRows] = useState(null);   // null = loading
  const [aiRows, setAiRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [teamNameCache] = useState(() => new Map());

  const months = useMemo(lastTwelveMonthKeys, []);

  useEffect(() => {
    let cancelled = false;
    setSmsRows(null);
    setAiRows(null);
    setLoadError(null);

    (async () => {
      try {
        // Both queries filter the collection group by `monthKey` to scope
        // to the picked month, then order by `count` desc to surface the
        // heavy hitters first. Firestore needs a composite index for this
        // (collection-group + where + orderBy); the first run will throw
        // with a console link to auto-create it.
        const smsQ = query(
          collectionGroup(db, 'smsUsage'),
          where('monthKey', '==', monthKey),
          orderBy('count', 'desc'),
          limit(TOP_N),
        );
        const aiQ = query(
          collectionGroup(db, 'aiUsage'),
          where('monthKey', '==', monthKey),
          orderBy('count', 'desc'),
          limit(TOP_N),
        );

        const [smsSnap, aiSnap] = await Promise.all([
          getDocs(smsQ),
          getDocs(aiQ),
        ]);
        if (cancelled) return;

        const smsList = smsSnap.docs.map((d) => {
          const x = d.data() || {};
          return {
            teamId: x.teamId || d.ref.parent.parent?.id || 'unknown',
            count:  Number(x.count || 0),
            plan:   x.plan || 'free',
          };
        });
        const aiList = aiSnap.docs.map((d) => {
          const x = d.data() || {};
          return {
            teamId: x.teamId || d.ref.parent.parent?.id || 'unknown',
            count:  Number(x.count || 0),
            plan:   x.plan || 'free',
          };
        });

        // Hydrate names (parallelized across both lists' team ids)
        const allTeamIds = Array.from(new Set([
          ...smsList.map((r) => r.teamId),
          ...aiList.map((r) => r.teamId),
        ])).filter(Boolean);
        await hydrateTeamNames(allTeamIds, teamNameCache);
        if (cancelled) return;

        setSmsRows(smsList.map((r) => ({ ...r, name: teamNameCache.get(r.teamId) || r.teamId })));
        setAiRows(aiList.map((r) => ({ ...r, name: teamNameCache.get(r.teamId) || r.teamId })));
      } catch (err) {
        if (cancelled) return;
        console.error('[AdminCostDashboard] load failed', err);
        setLoadError(err?.message || 'Failed to load usage data.');
      }
    })();
    return () => { cancelled = true; };
  }, [monthKey, teamNameCache]);

  // Headline numbers — totals across the top-N rows we loaded. This is
  // a LOWER BOUND if there are >TOP_N teams sending; we'd need a server-
  // side aggregation for the true total. For Phase 1 scale (under 100
  // active teams) the top-20 captures everything that matters.
  const totalSms = (smsRows || []).reduce((s, r) => s + r.count, 0);
  const totalAi  = (aiRows || []).reduce((s, r) => s + r.count, 0);
  const estSmsCost = totalSms * COST_PER_SMS_USD;
  const estAiCost  = totalAi * COST_PER_AI_MSG_USD;
  const estTotalCost = estSmsCost + estAiCost;

  const isCurrentMonth = monthKey === getCurrentMonthKey();

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="p-2 rounded-xl text-zinc-400 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </Link>
            <div className="h-12 w-12 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center">
              <ChartBarIcon className="h-6 w-6 text-[#5a8a1f]" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
                Admin
              </p>
              <h1 className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
                Cost Dashboard
              </h1>
              <p className="text-xs text-zinc-500">
                SMS + AI usage by team · {formatMonthLabel(monthKey)}
                {isCurrentMonth && <span className="ml-1 text-[#5a8a1f]">(current)</span>}
              </p>
            </div>
          </div>

          {/* Month picker */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-zinc-500" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              Month
            </label>
            <select
              value={monthKey}
              onChange={(e) => setMonthKey(e.target.value)}
              className="rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
            >
              {months.map((m) => (
                <option key={m} value={m}>{formatMonthLabel(m)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error banner */}
        {loadError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">Couldn't load usage data</p>
            <p className="text-xs text-red-700 mt-1">{loadError}</p>
            <p className="text-xs text-red-600 mt-2">
              First run? You may need to create the Firestore composite indexes for
              <code className="mx-1 px-1.5 py-0.5 rounded bg-red-100">collectionGroup('smsUsage')</code>
              and
              <code className="mx-1 px-1.5 py-0.5 rounded bg-red-100">collectionGroup('aiUsage')</code>.
              The console link in the error message above creates them in one click.
            </p>
          </div>
        )}

        {/* Headline cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <HeadlineCard
            icon={ChatBubbleLeftRightIcon}
            label="SMS sent"
            value={smsRows === null ? '—' : totalSms.toLocaleString()}
            sub={smsRows === null ? 'Loading…' : `est. ${formatUsd(estSmsCost)}`}
            color="blue"
          />
          <HeadlineCard
            icon={SparklesIcon}
            label="AI generations"
            value={aiRows === null ? '—' : totalAi.toLocaleString()}
            sub={aiRows === null ? 'Loading…' : `est. ${formatUsd(estAiCost)}`}
            color="lime"
          />
          <HeadlineCard
            icon={CurrencyDollarIcon}
            label="Estimated total"
            value={smsRows === null || aiRows === null ? '—' : formatUsd(estTotalCost)}
            sub={`top ${TOP_N} teams`}
            color="amber"
          />
        </div>

        {/* Methodology note — surfaces the assumptions so the numbers
            don't get mistaken for billing-grade truth. */}
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          <strong>Estimate basis:</strong> SMS at ${COST_PER_SMS_USD.toFixed(3)}/segment (Twilio + 10DLC blended),
          AI at ${COST_PER_AI_MSG_USD.toFixed(3)}/generation (claude-sonnet-5 avg prompt). Top-{TOP_N} teams only —
          totals lower-bound true cost when more than {TOP_N} teams are active in a month.
        </div>

        {/* Two columns: SMS table + AI table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <UsageTable
            title="Top teams by SMS volume"
            icon={ChatBubbleLeftRightIcon}
            rows={smsRows}
            unitLabel="SMS"
            costPerUnit={COST_PER_SMS_USD}
          />
          <UsageTable
            title="Top teams by AI generation"
            icon={SparklesIcon}
            rows={aiRows}
            unitLabel="msgs"
            costPerUnit={COST_PER_AI_MSG_USD}
          />
        </div>
      </div>
    </div>
  );
}

function HeadlineCard({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    blue:  'bg-blue-50 text-blue-700 ring-blue-200',
    lime:  'bg-[#b5f03d]/15 text-[#5a8a1f] ring-[#b5f03d]/40',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 flex items-center gap-4">
      <div className={`rounded-xl p-3 ring-1 flex-shrink-0 ${colorMap[color] || colorMap.lime}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-[#0d1f14] truncate" style={{ fontFamily: HEAD_FONT }}>{value}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function UsageTable({ title, icon: Icon, rows, unitLabel, costPerUnit }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#5a8a1f]" />
        <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
          {title}
        </h2>
      </div>

      {rows === null ? (
        <div className="px-5 py-10 text-center text-sm text-zinc-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin inline mr-2" />
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-zinc-400">
          No usage recorded for this month yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-4 py-2 text-left" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>Team</th>
              <th className="px-4 py-2 text-left" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>Plan</th>
              <th className="px-4 py-2 text-right" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>{unitLabel}</th>
              <th className="px-4 py-2 text-right" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>Est. cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.teamId} className="hover:bg-zinc-50">
                <td className="px-4 py-2.5">
                  <Link
                    to={`/teams/${r.teamId}`}
                    className="text-[#0d1f14] hover:text-[#5a8a1f] font-medium truncate inline-block max-w-[200px]"
                    title={r.name}
                  >
                    {r.name}
                  </Link>
                </td>
                <td className="px-4 py-2.5">
                  <PlanBadge plan={r.plan} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-[#0d1f14]">
                  {r.count.toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-zinc-600">
                  {formatUsd(r.count * costPerUnit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
